import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { getBanners, saveBanner, deleteBanner, updateBanner } from "../Apis/BannerAPIs";
import { uploadFile } from "../Apis/ManagerAPIs";
import { getImageUrl } from "../Apis/api";
import { toast } from "react-toastify";
import usePermissions from "../hook/userPermission";
import { getExposList } from "../Apis/ExpoAPIs";

const BannerLayer = () => {
  const { hasPermission } = usePermissions();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expoOptions, setExpoOptions] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    image: "",
    expoId: "",
  });
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  const fetchBanners = async () => {
    setLoading(true);
    const res = await getBanners();
    if (res.status) {
      setBanners(res.response?.data || res.response || []);
    }
    setLoading(false);
  };

  const fetchExpos = async () => {
    const res = await getExposList({ limit: 1000 });
    if (res.status) {
      const expoData = res.response?.data || res.response || [];
      setExpoOptions(
        expoData.filter(e => !e.isDeleted).map(e => ({
          value: e._id || e.id,
          label: e.expoName,
        }))
      );
    }
  };

  useEffect(() => {
    fetchBanners();
    fetchExpos();
  }, []);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 0) {
      setFiles([...files, ...selectedFiles]);
      const newPreviews = selectedFiles.map(file => URL.createObjectURL(file));
      setPreviews([...previews, ...newPreviews]);
    }
  };

  const removeFile = (index) => {
    const newFiles = [...files];
    const newPreviews = [...previews];
    newFiles.splice(index, 1);
    newPreviews.splice(index, 1);
    setFiles(newFiles);
    setPreviews(newPreviews);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.expoId) {
      toast.error("Please select an exhibition/expo");
      return;
    }
    if (!formData.title.trim()) {
      toast.error("Please enter a banner title");
      return;
    }
    if (!formData.description.trim()) {
      toast.error("Please enter a banner description");
      return;
    }

    // If not editing, at least one file is mandatory
    if (!isEditing && files.length === 0) {
      toast.error("Please choose at least one banner image");
      return;
    }

    setLoading(true);

    try {
      if (isEditing) {
        // Handle Update
        let imagePath = formData.image;

        if (files.length > 0) {
          const uploadFormData = new FormData();
          uploadFormData.append("file", files[0]);
          uploadFormData.append("folder", "banners");
          const uploadRes = await uploadFile(uploadFormData);
          if (uploadRes.status) {
            imagePath = uploadRes.response?.path || uploadRes.response?.data?.path || uploadRes.response;
          } else {
            setLoading(false);
            return;
          }
        }

        const res = await updateBanner(editId, {
          title: formData.title,
          description: formData.description,
          image: imagePath,
          expoId: formData.expoId
        });

        if (res.status) {
          toast.success("Banner updated successfully!");
          resetForm();
          fetchBanners();
        }
      } else {
        // Handle Multiple Save
        const uploadPromises = files.map(async (file) => {
          const uploadFormData = new FormData();
          uploadFormData.append("file", file);
          uploadFormData.append("folder", "banners");
          const uploadRes = await uploadFile(uploadFormData);

          if (uploadRes.status) {
            const imagePath = uploadRes.response?.path || uploadRes.response?.data?.path || uploadRes.response;
            return await saveBanner({
              title: formData.title,
              description: formData.description,
              image: imagePath,
              expoId: formData.expoId,
            });
          }
          return { status: false };
        });

        const results = await Promise.all(uploadPromises);
        const successCount = results.filter(r => r.status).length;

        if (successCount > 0) {
          toast.success("Banners saved successfully!");
          resetForm();
          fetchBanners();
        } else {
          toast.error("Failed to upload banners");
        }
      }
    } catch (error) {
      console.error("Operation error:", error);
      toast.error("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ title: "", description: "", image: "", expoId: "" });
    setFiles([]);
    setPreviews([]);
    setIsEditing(false);
    setEditId(null);
  };

  const handleEditClick = (banner) => {
    setFormData({
      title: banner.title || "",
      description: banner.description || "",
      image: banner.image || "",
      expoId: typeof banner.expoId === 'object' ? banner.expoId?._id || banner.expoId?.id || "" : banner.expoId || "",
    });
    setPreviews([getImageUrl(banner.image)]);
    setFiles([]);
    setIsEditing(true);
    setEditId(banner._id || banner.id);
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (deleteId) {
      const res = await deleteBanner(deleteId);
      if (res.status) {
        toast.success("Banner deleted successfully!");
        fetchBanners();
      }
      setShowDeleteConfirm(false);
      setDeleteId(null);
    }
  };

  return (
    <div className="card h-100 p-0 radius-12 overflow-hidden border-0 shadow-sm bg-light-50">
      {/* Custom Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1100, backdropFilter: 'blur(4px)' }}
        >
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '400px' }}>
            <div className="modal-content radius-16 border-0 shadow-2xl overflow-hidden">
              <div className="modal-body p-32 text-center">
                <div
                  className="bg-danger-50 w-80-px h-80-px radius-circle d-flex align-items-center justify-content-center mx-auto mb-24 border border-danger-100"
                >
                  <Icon icon="solar:trash-bin-2-bold-duotone" className="text-4xl text-danger" />
                </div>
                <h5 className="text-dark fw-bold mb-12">Delete Banner?</h5>
                <p className="text-secondary-light mb-32 px-12">
                  This action cannot be undone. Are you sure you want to remove this banner from your landing page?
                </p>
                <div className="d-flex align-items-center gap-3">
                  <button
                    className="btn btn-light radius-12 py-12 px-24 flex-grow-1 fw-bold text-secondary"
                    onClick={() => setShowDeleteConfirm(false)}
                  >
                    CANCEL
                  </button>
                  <button
                    className="btn btn-danger radius-12 py-12 px-24 flex-grow-1 fw-bold shadow-sm"
                    onClick={confirmDelete}
                  >
                    DELETE NOW
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div
        className="card-header border-bottom py-12 px-24 d-flex align-items-center justify-content-between"
        style={{
          background: "linear-gradient(135deg, #000000 0%, #bb0007 50%, #ed1c24 100%)",
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <Icon icon="solar:gallery-wide-bold-duotone" className="text-white text-xl" />
          <h6 className="text-white mb-0 fw-bold uppercase tracking-wider" style={{ fontSize: '13px' }}>Landing Page Banners</h6>
        </div>
        {/* <div className="d-flex align-items-center gap-2">
            <span className="badge bg-white bg-opacity-20 text-white px-12 py-4 radius-pill fw-bold" style={{ fontSize: '10px' }}>
                {banners.length} Active Banners
            </span>
        </div> */}
      </div>

      <div className="card-body p-16">
        {/* Compact Horizontal Toolbar */}
        {((!isEditing && hasPermission("banners", "add")) || (isEditing && hasPermission("banners", "edit"))) && (
          <div className="bg-white p-24 radius-16 shadow-sm mb-32 border border-light-200">
            <form onSubmit={handleSubmit} className="row g-4">
              <div className="col-md-12">
                <div className="form-group">
                  <label className="form-label fw-bold text-dark text-uppercase mb-8 d-flex align-items-center gap-1" style={{ fontSize: "1rem" }}>
                    <Icon icon="solar:globus-bold" className="text-danger" />
                    SELECT EXPO <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select radius-12 border-light-200 py-12 px-16 text-sm"
                    value={formData.expoId}
                    onChange={(e) => setFormData({ ...formData, expoId: e.target.value })}
                    style={{ backgroundColor: '#fcfcfc' }}
                  >
                    <option value="">-- Choose Exhibition / Expo --</option>
                    {expoOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label className="form-label fw-bold text-dark text-uppercase mb-8 d-flex align-items-center gap-1" style={{ fontSize: "1rem" }}>
                    <Icon icon="solar:pen-new-square-bold" className="text-danger" />
                    BANNER TITLE <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control radius-12 border-light-200 py-12 px-16 text-sm"
                    placeholder="Enter a catchy headline..."
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    style={{ backgroundColor: '#fcfcfc' }}
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label className="form-label fw-bold text-dark text-uppercase mb-8 d-flex align-items-center gap-1" style={{ fontSize: "1rem" }}>
                    <Icon icon="solar:notes-bold" className="text-danger" />
                    BANNER DESCRIPTION <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control radius-12 border-light-200 py-12 px-16 text-sm"
                    placeholder="Briefly describe the promotion..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    style={{ backgroundColor: '#fcfcfc' }}
                  />
                </div>
              </div>

              {/* Media Upload Area */}
              <div className="col-12">
                <label className="form-label fw-bold text-dark text-uppercase mb-8 d-flex align-items-center gap-1" style={{ fontSize: "1rem" }}>
                  <Icon icon="solar:gallery-bold" className="text-danger" />
                  UPLOAD MEDIA <span className="text-danger">*</span>
                </label>
                <div
                  className="position-relative border-1 border-dashed radius-12 d-flex align-items-center justify-content-center p-20 transition-all hover-bg-light-50 group"
                  style={{ borderColor: "#ed1c2444", cursor: 'pointer', minHeight: '80px' }}
                >
                  <div className="text-center">
                    <Icon icon="solar:upload-minimalistic-bold" className="text-2xl text-secondary mb-8 group-hover-text-danger" />
                    <p className="mb-0 text-xs fw-semibold text-dark">
                      {files.length > 0 ? (
                        <span className="text-danger">{files.length} Selected</span>
                      ) : (
                        <>Drop your banner here or <span className="text-danger">Browse</span></>
                      )}
                    </p>
                    <span className="text-xxs text-secondary">Recommended: 1920 x 600px (PNG, JPG)</span>
                  </div>
                  <input
                    type="file"
                    className="position-absolute top-0 start-0 w-100 h-100 opacity-0 cursor-pointer"
                    onChange={handleFileChange}
                    accept="image/*"
                    multiple
                    title=""
                  />
                </div>
                {previews.length > 0 && (
                  <div className="mt-16 d-flex flex-wrap gap-12">
                    {previews.map((src, idx) => (
                      <div key={idx} className="position-relative group-preview" style={{ width: '120px', height: '60px' }}>
                        <img src={src} alt="preview" className="w-100 h-100 object-fit-cover radius-12 border border-light shadow-sm" />
                        <button
                          type="button"
                          className="btn btn-sm btn-danger position-absolute top-n8 end-n8 radius-circle p-0 d-flex align-items-center justify-content-center shadow-lg"
                          style={{ width: '24px', height: '24px', border: '2px solid white', zIndex: 5 }}
                          onClick={() => removeFile(idx)}
                        >
                          <Icon icon="solar:trash-bin-trash-bold" style={{ fontSize: '12px', color: 'white' }} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="col-12 d-flex justify-content-end align-items-center gap-3">
                {isEditing && (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="btn btn-outline-secondary radius-12 px-24 py-10 fw-bold text-xs"
                  >
                    Cancel Edit
                  </button>
                )}
                <button
                  type="submit"
                  className="btn radius-12 px-40 py-12 fw-bold text-white shadow-lg border-0 transition-all transform hover-translate-y-n2"
                  style={{
                    background: "linear-gradient(135deg, #bb0007, #ed1c24)",
                    fontSize: '13px',
                    letterSpacing: '0.5px'
                  }}
                  disabled={loading || (!isEditing && files.length === 0)}
                >
                  {loading ? (
                    <span className="spinner-border spinner-border-sm" role="status"></span>
                  ) : (
                    <div className="d-flex align-items-center gap-2">
                      <Icon icon={isEditing ? "solar:pen-bold" : "solar:diskette-bold"} className="text-lg" />
                      {isEditing ? "UPDATE BANNER" : "SAVE BANNER"}
                    </div>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Banner Gallery - Proper Landing Page Preview */}
        <div className="row g-4 mt-8">
          {banners.map((banner) => (
            <div key={banner._id || banner.id} className="col-12 col-xl-4 col-md-6">
              <div className="banner-preview-card radius-16 overflow-hidden position-relative shadow-sm bg-white border h-100 transition-all hover-shadow-md">
                <div className="position-relative" style={{ height: '180px' }}>
                  <img
                    src={getImageUrl(banner.image)}
                    alt={banner.title}
                    className="w-100 h-100 object-fit-cover transition-all"
                  />
                  <div className="position-absolute top-0 start-0 w-100 h-100 bg-dark opacity-0 transition-all card-overlay"></div>

                  {/* Small Overlay Badge */}
                  <div className="position-absolute top-0 start-0 m-12">
                    <div className="badge bg-danger text-white px-8 py-4 radius-4 fw-bold uppercase shadow-sm" style={{ fontSize: '8px', letterSpacing: '1px' }}>PROMPT EVENT</div>
                  </div>

                  {/* Actions Overlay */}
                  <div className="position-absolute top-0 end-0 m-12 d-flex gap-2 overlay-actions transition-all opacity-0">
                    {hasPermission("banners", "edit") && (
                      <button
                        onClick={() => handleEditClick(banner)}
                        className="btn btn-sm radius-8 shadow-sm p-8 d-flex border-0"
                        style={{ backgroundColor: '#dae4f7ff', color: 'black' }}
                        title="Edit"
                      >
                        <Icon icon="solar:pen-bold" className="text-md" />
                      </button>
                    )}
                    {hasPermission("banners", "delete") && (
                      <button
                        onClick={() => handleDelete(banner._id || banner.id)}
                        className="btn btn-sm btn-danger radius-8 shadow-sm p-8 d-flex"
                        title="Delete"
                      >
                        <Icon icon="solar:trash-bin-trash-bold" className="text-md" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Text Section Below Image */}
                <div className="p-16">
                  <h6 className="text-dark fw-bold mb-4 text-truncate" style={{ fontSize: '15px' }}>{banner.title || "Summer Exhibition 2026"}</h6>
                  {banner.expoId && (
                    <div className="text-danger fw-bold text-xxs mb-8 d-flex align-items-center gap-1" style={{ fontSize: '11px' }}>
                      <Icon icon="solar:globus-bold" />
                      LINKED EXPO: {typeof banner.expoId === 'object' ? banner.expoId.expoName : banner.expoId}
                    </div>
                  )}
                  <p className="text-secondary text-opacity-75 mb-0 line-clamp-2" style={{ fontSize: '12px', lineHeight: '1.5' }}>
                    {banner.description || "No description provided."}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {banners.length === 0 && !loading && (
            <div className="col-12">
              <div className="text-center py-80 bg-white radius-16 border border-dashed">
                <div className="bg-light-50 w-80-px h-80-px radius-circle d-flex align-items-center justify-content-center mx-auto mb-16 shadow-sm">
                  <Icon icon="solar:gallery-wide-bold" className="text-4xl text-secondary-light" />
                </div>
                <h5 className="text-dark fw-bold mb-4">No active banners.</h5>
                <p className="text-secondary-light mb-0">Start by creating a new banner for your landing page.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .banner-preview-card:hover img {
            transform: scale(1.05);
        }
        .banner-preview-card:hover .card-overlay {
            opacity: 0.4 !important;
        }
        .banner-preview-card:hover .overlay-actions {
            opacity: 1 !important;
            transform: translateY(0) !important;
        }
        .banner-preview-card {
            transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            border: 1px solid rgba(0,0,0,0.05);
        }
        .overlay-actions {
            z-index: 15;
            transform: translateY(-10px);
            transition: all 0.3s ease;
        }
        .card-overlay {
            pointer-events: none;
            z-index: 5;
        }
      `}</style>
    </div>
  );
};

export default BannerLayer;
